import { useState, useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../../i18n/useDateFormatter";
import { useDebounce } from "@reactuses/core";
import moment from "moment";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { DatesProvider } from "../../../../../dates/DatesContext";
import { useDates } from "../../../../../dates/DatesContext";
import BreadcrumbDates, { type Breadcrumb } from "../../../../../components/core/blocks/BreadcrumbDates";
import getMetricSpec from "../../../../../modules/merchandising/utils/metrics";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import EmptyState from "../../../../../components/custom/merchandise/EmptyState";

import MetricsCard from "../../../../../components/custom/merchandise/MetricsCard";
import AppStatsCard from "../../../../../components/custom/merchandise/AppStatsCard";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../../../../utils/dashboardLinks";
import useCategories from "../../../../../hooks/useCategories";
import VerifyEmailModal from "../../../../../modules/onboarding/components/VerifyEmailModal";
import { useIsSPAPIConnected } from "../../../../../hooks/useIsSPAPIConnected";
import { AnnotationsTable } from "../../../../../components/custom/growth/AnnotationsTable";
import SalesMapUSA from "../../../../../components/custom/maps/usa";

import { useAppSelector } from "../../../../../redux/hooks";
import { getDashboardTo, DASHBOARD_TYPES } from "../../../../../modules/merchandising/components/cells/ProductCell";
import ExtensionInfo from "../../../../../modules/onboarding/components/ExtensionInfo";
import { css } from "../../../../../../styled-system/css";
import ProductsTable from "../../../../../modules/merchandising/components/tables/ProductsTable";
import { usePersistedTableState } from "../../../../../components/table/usePersistedTableState";
import { idsToVisibilityState, selectionStateToSelectedIds } from "../../../../../components/table/utils";
import { createColumnBuilder } from "../../../../../components/table/columnBuilder";
import { useGetV4ProductReportByDate } from "../../../../../modules/perf_reports/api/product/v4ProductReportByDate";
import { useGetV4ProductReportTotals } from "../../../../../modules/perf_reports/api/product/v4ProductReportTotals";
import { useGetV4CategoryReportByDate } from "../../../../../modules/perf_reports/api/category/v4CategoryReportByDate";
import { useGetV4CategoryReportTotals } from "../../../../../modules/perf_reports/api/category/v4CategoryReportTotals";
import { Flex } from "../../../../../../styled-system/jsx";
import ExternalProductLink from "../../../../../components/custom/merchandise/ExternalProductLink";
import { ButtonGroup } from "../../../../../components/core/basic/ButtonGroup";
import { ProductCell } from "../../../../../modules/merchandising/components/cells/ProductCell";
import { TrendCell } from "../../../../../modules/merchandising/components/cells/TrendCell";
import { formatNumber } from "../../../../../utils/formatNumber";
import { Accordion } from "../../../../../components/core/basic/Accordion";
import { useStandardEntityMetrics } from "../../../../../modules/merchandising/components/tables/metricUtils";
import { useGetAnnotations } from "../../../../../modules/annotations/api/useGetAnnotations";
import type { ProductQueryV4Row } from "../../../../../modules/perf_reports/api/product/v4ProductReportByProduct";
import { useAcosOrRoas } from "../../../../../utils/acosOrRoas";
import { PageContainer } from "../../../../../components/core/basic/PageContainer";
import { useModuleEnabled } from "../../../../../redux/selectors/modules";

const columnHelper = createColumnBuilder<ProductQueryV4Row>();

const Dashboard = () => {
  const { t } = useTranslation(["merchandising"]);
  const user = useAppSelector((state) => state.user);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const { category } = Route.useParams();

  const { group, start, end } = useDates();
  const longDate = useDateFormatter("long");
  const [annotationsTab, setAnnotationsTab] = useState("advertisement");
  const acosOrRoas = useAcosOrRoas();

  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    category,
    "merchandising",
  );

  const defaultVisibleColumns = [
    "sales",
    "units",
    "margin",
    "cost",
    "roms",
    "page_views",
    "conversion_rate",
    "ad_spend",
    "ad_sales",
    "acos",
    "ctr",
    "clicks",
    "impressions",
    "orders",
    "total_acos",
  ];
  const persistKey = "merchDashboardCategoryProducts";

  const standardMetricIds = useStandardEntityMetrics("product");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("product_title", {
        header: () => t(($) => $.merchandising["Product"]),
        enableHiding: false,
        minSize: 100,
        size: 500,
        cell: (props) => {
          const { product_title } = props.row.original;

          return (
            <ProductCell
              original={{
                ...props.row.original,
                product_id: props.row.original.product_id.toString(),
                pricing_plan__status: props.row.original.pricing_plan_status,
                price_override_end:
                  props.row.original.price_override_end ? props.row.original.price_override_end.slice(0, 10) : "",
                cellContext: "merchandising",
              }}
              value={product_title}
              showInventoryBadge={false}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            label: t(($) => $.merchandising["Product Title"]),
          },
        },
      }),
      columnHelper.accessor("parent_asin", {
        id: "parent_asin",
        header: t(($) => $.merchandising["Parent ASIN"]),
        enableHiding: channel === "amazon",
        enableResizing: false,
        size: 110,
        cell: ({ getValue }) => {
          const asin = getValue();

          if (!asin) {
            return null;
          }

          return (
            <Flex gap={"small"} alignItems={"center"}>
              <span>{asin}</span>
              <span className={css({ paddingBottom: "xsmall" })}>
                <ExternalProductLink asin={asin} />
              </span>
            </Flex>
          );
        },
      }),
      // Invisible columns to support filtering
      columnHelper.accessor((row) => row, {
        id: "inventory_status",
        header: t(($) => $.merchandising["Inventory State"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),

      columnHelper.accessor((row) => row, {
        id: "sales_state",
        header: t(($) => $.merchandising["Sales State"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      tagsEnhancedEnabled ?
        columnHelper.accessor("tags_enhanced", {
          id: "tags_enhanced.id",
          header: t(($) => $.merchandising["Tags"]),
          enableHiding: false,
          meta: {
            columnFilterOptions: {
              format: "multi-select",
              label: t(($) => $.merchandising["Tags"]),
            },
          },
        })
      : columnHelper.accessor((row) => row, {
          id: "tags",
          header: t(($) => $.merchandising["Tags"]),
          enableHiding: false,
          meta: {
            columnFilterOptions: {
              format: "multi-select",
            },
          },
        }),
      columnHelper.accessor((row) => row, {
        id: "variations",
        header: t(($) => $.merchandising["Variants"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),

      columnHelper.accessor("pricing_plan_status", {
        id: "pricing_plan_status",
        header: t(($) => $.merchandising["Enrollment State"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
      columnHelper.accessor("product_quantity_fba", {
        id: "product_quantity_fba",
        header: t(($) => $.merchandising["Product Quantity FBA"]),
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      columnHelper.accessor("product_quantity", {
        id: "product_quantity",
        header: t(($) => $.merchandising["Product Quantity"]),
        cell: ({ getValue }) => formatNumber(getValue()),
      }),
      columnHelper.accessor("trends_score", {
        id: "trends_score",
        header: t(($) => $.merchandising["Trends"]),
        enableResizing: false,
        size: 75,
        cell: (props) => {
          const { margin_trend, sales_trend, trends_score } = props.row.original;

          const trends = [];

          if (margin_trend && margin_trend >= 1) {
            trends.push(t(($) => $.merchandising["Margin is trending up"]));
          }
          if (margin_trend && margin_trend <= -1) {
            trends.push(t(($) => $.merchandising["Margin is trending down"]));
          }

          if (sales_trend && sales_trend >= 1) {
            trends.push(t(($) => $.merchandising["Sales are trending up"]));
          }
          if (sales_trend && sales_trend <= -1) {
            trends.push(t(($) => $.merchandising["Sales are trending down"]));
          }
          return <TrendCell value={trends_score} trends={trends} />;
        },
      }),
    ];
  }, [channel, standardMetricIds, tagsEnhancedEnabled, t]);

  const defaultHiddenCols = idsToVisibilityState(
    defaultVisibleColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );

  const initialStateConfig = {
    columnVisibility: defaultHiddenCols,
    columnOrder: defaultVisibleColumns,
    columnPinning: {
      start: ["product_title"],
      end: [],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: persistKey,
    initialState: initialStateConfig,
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);

  const productQueryFilters = useMemo(() => {
    const selectedIdsFilter =
      selectedIds.length ? [{ id: "product_id", value: { op: "anyof", value: selectedIds } }] : [];
    const categoryFilter = [{ id: "category_id", value: { op: "=", value: category } }];
    return [...state.columnFilters, ...selectedIdsFilter, ...categoryFilter];
  }, [state.columnFilters, selectedIds, category]);

  const categoryQueryFilters = useMemo(() => {
    const categoryFilter = [{ id: "category_id", value: { op: "=", value: category } }];
    return [...state.columnFilters, ...categoryFilter];
  }, [state.columnFilters, category]);

  // Conditionally fetch product or category report data based on selection, filters, or search
  const useProductReports = selectedIds.length > 0 || state.columnFilters.length > 0 || Boolean(debounceSearch);

  const productByDateOptionsGetter = useGetV4ProductReportByDate();
  const productsByDateReportQuery = useQuery({
    ...productByDateOptionsGetter({
      filters: productQueryFilters,
      search: debounceSearch,
    }),
    enabled: useProductReports,
    placeholderData: keepPreviousData,
  });

  const productTotalOptionsGetter = useGetV4ProductReportTotals();
  const productTotalsReportQuery = useQuery({
    ...productTotalOptionsGetter({
      filters: productQueryFilters,
      search: debounceSearch,
    }),
    enabled: useProductReports,
    placeholderData: keepPreviousData,
  });

  const categoryByDateOptionsGetter = useGetV4CategoryReportByDate();
  const categoryByDateReportQuery = useQuery({
    ...categoryByDateOptionsGetter({
      filters: categoryQueryFilters,
      search: debounceSearch,
    }),
    enabled: !useProductReports,
    placeholderData: keepPreviousData,
  });

  const categoryTotalOptionsGetter = useGetV4CategoryReportTotals();
  const categoryTotalsReportQuery = useQuery({
    ...categoryTotalOptionsGetter({
      filters: categoryQueryFilters,
      search: debounceSearch,
    }),
    enabled: !useProductReports,
    placeholderData: keepPreviousData,
  });

  const data = useMemo(() => {
    if (useProductReports) {
      if (productTotalsReportQuery.data?.data && productsByDateReportQuery.data?.data) {
        return {
          total: productTotalsReportQuery.data.data,
          date: productsByDateReportQuery.data.data,
        };
      }
    } else if (categoryTotalsReportQuery.data?.data && categoryByDateReportQuery.data?.data) {
      return {
        total: categoryTotalsReportQuery.data.data,
        date: categoryByDateReportQuery.data.data,
      };
    }
    return null;
  }, [
    useProductReports,
    productTotalsReportQuery.data?.data,
    productsByDateReportQuery.data?.data,
    categoryTotalsReportQuery.data?.data,
    categoryByDateReportQuery.data?.data,
  ]);

  const getAnnotationsOptions = useGetAnnotations();
  const annotationQuery = useQuery({
    ...getAnnotationsOptions({ category }),
    placeholderData: keepPreviousData,
  });

  const annotationCounts = useMemo(() => {
    const data = annotationQuery.data?.flatMap((date) => date.annotations) ?? [];

    return data.reduce(
      (acc, annotation) => {
        if (annotation.product) {
          acc.productCount++;
        }
        if (annotation.advertisement) {
          acc.advertisementCount++;
        }
        return acc;
      },
      { productCount: 0, advertisementCount: 0 },
    );
  }, [annotationQuery.data]);

  const { overviewMetrics } = useMemo(() => {
    if (channel === "shopify") {
      return {
        overviewMetrics: {
          metrics: [getMetricSpec("sales"), getMetricSpec("units"), getMetricSpec("margin")],
        },
      };
    }

    const roasMetrics = [getMetricSpec("troas"), getMetricSpec("roas")];
    const acosMetrics = [getMetricSpec("total_acos"), getMetricSpec("acos")];

    return {
      overviewMetrics: {
        metrics: [
          getMetricSpec("sales"),
          getMetricSpec("units"),
          getMetricSpec("margin"),
          ...acosOrRoas(acosMetrics, roasMetrics),
          getMetricSpec("ad_sales"),
          getMetricSpec("ad_spend"),
          ...acosOrRoas(roasMetrics, acosMetrics),
          getMetricSpec("roms"),
          getMetricSpec("cost"),
          getMetricSpec("promo_cost"),
          getMetricSpec("ctr"),
          getMetricSpec("orders"),
          getMetricSpec("cpc"),
          getMetricSpec("conversion_rate"),
        ],
      },
    };
  }, [acosOrRoas, channel]);

  const breadCrumbItems: Breadcrumb[] = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.merchandising["Merchandising Dashboard"]),
      href: getDashboardTo({
        resourceId: undefined,
        type: DASHBOARD_TYPES.ROOT,
      }),
      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: t(($) => $.merchandising["Dashboards"]),
    },
  ];

  breadCrumbItems.push({
    name: categoryDetails.leaf_category_name ?? "",
    href: categoryDetails.href,
    dropdownLinkOptions: categoryData,
    searchable: true,
    searchPlaceholderText: t(($) => $.merchandising["Search categories..."]),
    dropdownSubtitle: t(($) => $.merchandising["Categories"]),
  });

  const isSPAPIConnected = useIsSPAPIConnected();

  const showEmpty = useMemo((): false | "COLLECTING_DATA" | "NO_DATA" => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace.marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      }
      return "COLLECTING_DATA";
    }
    if (isSPAPIConnected) {
      return "COLLECTING_DATA";
    }
    return "NO_DATA";
  }, [user, marketPlace.marketPlace, isSPAPIConnected]);

  if (user.organization_type === "vendor" && channel === "walmart") {
    return <Navigate to="/user/advertising/dashboard" replace />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} />
      <PageContainer withBreadcrumb>
        {showEmpty ?
          <EmptyState type={showEmpty} />
        : <>
            <MetricsCard
              title={t(($) => $.merchandising["Overview"])}
              byline={
                <>
                  {longDate.format(start.toDate())}
                  {" - "}
                  {longDate.format(end.toDate())}
                </>
              }
              metrics={overviewMetrics.metrics}
              totals={{
                data: data?.total,
                loading:
                  useProductReports ?
                    productTotalsReportQuery.isFetching &&
                    (!productTotalsReportQuery.data || productTotalsReportQuery.isPlaceholderData)
                  : categoryTotalsReportQuery.isFetching &&
                    (!categoryTotalsReportQuery.data || categoryTotalsReportQuery.isPlaceholderData),
              }}
              timeline={{
                data: data?.date ?? [],
                loading:
                  useProductReports ?
                    productsByDateReportQuery.isFetching &&
                    (!productsByDateReportQuery.data || productsByDateReportQuery.isPlaceholderData)
                  : categoryByDateReportQuery.isFetching &&
                    (!categoryByDateReportQuery.data || categoryByDateReportQuery.isPlaceholderData),
              }}
              initialSelectedMetric={
                channel !== "shopify" ?
                  ["sales", "units", acosOrRoas("tacos", "troas"), "margin"]
                : ["sales", "units", "margin"]
              }
              showAnnotations={true}
              group={group}
              showMetricsSelector={channel !== "shopify"}
              id={"merchProductsTable"}
              context={"category"}
              extendMetrics={channel !== "shopify"}
            >
              <Accordion title={t(($) => $.merchandising["Products"])} defaultExpanded>
                <ProductsTable
                  categoryId={category}
                  persistKey={persistKey}
                  state={state}
                  initialState={initialState}
                  setters={setters}
                  columns={columns}
                />
              </Accordion>

              {channel !== "shopify" && (
                <Accordion title={t(($) => $.merchandising["Impact Analysis"])}>
                  <ButtonGroup
                    onSelect={(value) => {
                      setAnnotationsTab(value);
                    }}
                    value={annotationsTab}
                    buttons={[
                      {
                        value: "advertisement",
                        label: t(($) => $.merchandising["Media Plans"]),
                        counter: annotationCounts["advertisementCount"],
                      },

                      {
                        value: "product",
                        label: t(($) => $.merchandising["Products"]),
                        counter: annotationCounts["productCount"],
                      },
                    ]}
                    colorPalette={"buttonGroup.fuchsia"}
                  />
                  <AnnotationsTable
                    data={annotationQuery.data}
                    isLoading={annotationQuery.isLoading}
                    annotationType={annotationsTab}
                  />
                </Accordion>
              )}
              {marketPlace.marketPlace === "USA" && channel === "amazon" && (
                <Accordion title={t(($) => $.merchandising["Sales By State"])}>
                  <SalesMapUSA
                    start_date={start.format("YYYY-MM-DD")}
                    end_date={end.format("YYYY-MM-DD")}
                    marketPlace={marketPlace}
                    categoryId={category}
                    productId={null}
                  />
                </Accordion>
              )}
            </MetricsCard>
            <AppStatsCard category={category} />
          </>
        }
      </PageContainer>
    </>
  );
};

const MerchDashboardCategory: FC = () => {
  return (
    <DatesProvider>
      <VerifyEmailModal />
      <ExtensionInfo />
      <Dashboard />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/merchandising/category/$category")({
  component: MerchDashboardCategory,
});
